import {Accordion} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import DataView from 'analysis-tool/data-view/DataView';
import DataViewContext, {DEFAULT_BLOCK_CONFIG} from 'analysis-tool/data-view/DataViewContext';
import {assertAndContinue, assert} from 'Asserts';
import HelpButton from 'help/HelpButton';
import {DEFAULT_BLOCK_HEIGHT} from 'insights/InsightsConstants';
import {FONT_PROXIMA_19, FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {WHITE} from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';
import {assertNever} from 'util/Types';

import ConfigFormBlock from './ConfigFormBlock';
import {useDataLayoutContext} from './DataLayoutContext';
import {Block} from './DataLayoutTypes';

interface GridBlockProps {
  block: Block;
  layoutPath: Array<string | number>;
}

function BlockContents({block, layoutPath}: GridBlockProps): JSX.Element | null {
  assert(block.type !== 'DATA_VIEW', 'Data view blocks should be rendered in a DataViewContext.Provider');
  if (block.type === 'CONFIG_FORM') {
    return <ConfigFormBlock block={block} layoutPath={layoutPath} />;
  } else if (block.type === 'MARKDOWN') {
    return <MarkdownContent content={block.markdown} />;
  } else {
    assertAndContinue(false, 'Found unexpected block type in layout', {
      blockType: assertNever(block).type,
    });
    return null;
  }
}

interface GridBlockHeaderProps {
  title?: string;
  subtitle?: string;
  infoText?: string;
}

export function GridBlockHeaderOrNull({title, subtitle, infoText}: GridBlockHeaderProps): JSX.Element | null {
  if (!title && !subtitle) {
    return null;
  }
  assert(title != null || infoText == null, 'infoText cannot be present without a title');

  return (
    <div>
      {title && (
        <div className={css(styles.gridBlockTitleAndInfoText)}>
          {title && <div className={css(styles.gridBlockTitle)}>{title}</div>}
          {infoText && (
            <HelpButton
              renderContent={() => <span className="u-pre-wrap u-normal-font-weight">{infoText}</span>}
              iconClassName="u-mini-spacing-left"
            />
          )}
        </div>
      )}
      {subtitle && <div className={css(styles.gridBlockSubtitle)}>{subtitle}</div>}
    </div>
  );
}

export function GridBlockBody({
  children,
  collapsingBodyTitle,
  shouldSetHeight,
}: {
  children: React.ReactNode;
  collapsingBodyTitle?: string;
  shouldSetHeight?: boolean;
}): JSX.Element {
  if (!collapsingBodyTitle && !shouldSetHeight) {
    return <>{children}</>;
  }

  if (!collapsingBodyTitle) {
    return <div className={css(shouldSetHeight && styles.gridContentHeight)}>{children}</div>;
  }

  return (
    <Accordion
      className={css(styles.accordion)}
      classNames={{
        panel: css(styles.accordionPanel, shouldSetHeight && styles.gridContentHeight),
        item: css(styles.accordionItem),
        content: css(styles.accordionContent),
      }}
      variant="filled"
    >
      <Accordion.Item value="block">
        <Accordion.Control>{collapsingBodyTitle}</Accordion.Control>
        <Accordion.Panel>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

/** @package */
export default function GridBlock({block, layoutPath}: GridBlockProps): JSX.Element | null {
  const {actionOrigin} = useDataLayoutContext();
  const dataViewContextValue = useMemo(() => {
    if (block.type === 'DATA_VIEW') {
      return {
        actionOrigin,
        analysisView: block.view,
        blockConfig: block.blockConfig
          ? {...block.blockConfig, shouldSetHeight: true}
          : {...DEFAULT_BLOCK_CONFIG, shouldSetHeight: true},
      };
    } else {
      return null;
    }
  }, [actionOrigin, block]);

  if (block.type === 'DATA_VIEW') {
    assert(dataViewContextValue != null, 'Data view context value should be set for DATA_VIEW blocks');
    return (
      <DataViewContext.Provider value={dataViewContextValue}>
        <DataView />
      </DataViewContext.Provider>
    );
  }

  return (
    <div className={css(styles.gridBlock)}>
      <GridBlockHeaderOrNull
        title={block.blockConfig?.title ?? undefined}
        subtitle={block.blockConfig?.subtitle ?? undefined}
        infoText={block.blockConfig?.infoText ?? undefined}
      />
      <GridBlockBody collapsingBodyTitle={block.blockConfig?.collapsingBodyTitle ?? undefined}>
        <BlockContents block={block} layoutPath={layoutPath} />
      </GridBlockBody>
    </div>
  );
}

export const styles = StyleSheet.create({
  gridBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
  },
  gridBlockTitle: {
    ...FONT_PROXIMA_19,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  gridBlockTitleAndInfoText: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  gridBlockSubtitle: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
  },
  accordion: {
    height: '100%',
  },
  accordionItem: {
    backgroundColor: WHITE,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  accordionPanel: {
    flexGrow: 1,
  },
  gridContentHeight: {
    // The GridSection sets the max height for the whole block, but we need to set an absolute height
    // for the Chart DATA_VIEW blocks because we need to pass in an absolute height/width for plotly.
    height: DEFAULT_BLOCK_HEIGHT,
  },
  accordionContent: {
    height: '100%',
  },
});
